import React, { useEffect, useMemo, useState } from "react";
import { TextField, Button, Select, MenuItem, IconButton, InputLabel, FormControl, Dialog, DialogTitle, DialogContent, DialogActions, Stack, Typography } from "@mui/material";
import { Delete as DeleteIcon, Add as AddIcon } from "@mui/icons-material";
import api from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";
import { useTheme } from "@mui/material/styles";

type Props = {
  initial?: any;
  onSubmit: (form: any) => Promise<void>;
  saving?: boolean;
};

type Supplier = { _id: string; name: string };
type Item = { _id: string; name: string; sku: string; costPrice?: number; salePrice?: number };

const emptyLine = { item: "", quantity: 1, unitPrice: 0 };

const InventoryQuickAddModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onCreated: (item: Item) => void;
}> = ({ open, onClose, onCreated }) => {
  const { token } = useAuth();
  const [form, setForm] = useState({
    name: "",
    category: "",
    costPrice: 0,
    price: 0,
    stock: 0,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setForm({ name: "", category: "", costPrice: 0, price: 0, stock: 0 });
    setError(null);
  };

  const closeAndReset = () => {
    reset();
    onClose();
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: Number(e.target.value) }));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError("Name is required");
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        category: form.category.trim() || undefined,
        costPrice: Number(form.costPrice) || 0,
        price: Number(form.price) || Number(form.costPrice) || 0,
        stock: Number(form.stock) || 0,
        status: "active",
      };
      const res = await api.post("/inventory", payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onCreated(res.data);
      closeAndReset();
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to create item");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={closeAndReset} fullWidth maxWidth="sm">
      <DialogTitle>Add Inventory Item</DialogTitle>
      <DialogContent>
        <Stack component="form" onSubmit={submit} spacing={2} mt={1}>
          <TextField
            name="name"
            label="Item Name"
            value={form.name}
            onChange={handleChange}
            required
            fullWidth
            size="small"
          />
          <TextField
            name="category"
            label="Category"
            value={form.category}
            onChange={handleChange}
            fullWidth
            size="small"
          />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              name="costPrice"
              label="Cost Price"
              type="number"
              value={form.costPrice}
              onChange={handleNumber}
              inputProps={{ step: 0.01, min: 0 }}
              size="small"
              fullWidth
            />
            <TextField
              name="price"
              label="Selling Price"
              type="number"
              value={form.price}
              onChange={handleNumber}
              inputProps={{ step: 0.01, min: 0 }}
              size="small"
              fullWidth
            />
          </Stack>
          <TextField
            name="stock"
            label="Initial Stock"
            type="number"
            value={form.stock}
            onChange={handleNumber}
            inputProps={{ min: 0 }}
            size="small"
            fullWidth
          />
          {error && (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          )}
          <DialogActions sx={{ px: 0, pb: 0 }}>
            <Button onClick={closeAndReset} color="inherit" disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? "Saving..." : "Add Item"}
            </Button>
          </DialogActions>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

const PurchaseOrderForm: React.FC<Props> = ({ initial, onSubmit, saving }) => {
  const { token } = useAuth();
  const theme = useTheme();
  const dark = theme.palette.mode === "dark";
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [quickAddOpen, setQuickAddOpen] = useState(false);

  const [form, setForm] = useState<any>(initial || {
    supplier: "", // supplier id or new supplier name
    newSupplier: "",
    items: [ { ...emptyLine } ],
    expectedDate: "",
    notes: "",
  });

  // Load suppliers and items
  useEffect(() => {
    const headers = { Authorization: `Bearer ${token}` };
    api.get("/inventory/suppliers/all", { headers }).then(res => setSuppliers(res.data || []));
    api.get("/inventory", { headers }).then(res => setItems(res.data || []));
  }, [token]);

  const handleField = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const updateLine = (idx: number, patch: any) => {
    const next = form.items.slice();
    next[idx] = { ...next[idx], ...patch };
    setForm({ ...form, items: next });
  };

  const addLine = () => setForm({ ...form, items: [...form.items, { ...emptyLine }] });
  const removeLine = (idx: number) => setForm({ ...form, items: form.items.filter((_: any, i: number) => i !== idx) });

  const handleItemCreated = (created: Item) => {
    setItems((prev) => [created, ...prev.filter((item) => item._id !== created._id)]);
    setForm((prev: any) => {
      const lines = [...(prev.items || [])];
      const targetIdx = lines.findIndex((line: any) => !line.item);
      if (targetIdx >= 0) {
        lines[targetIdx] = {
          ...lines[targetIdx],
          item: created._id,
          unitPrice: lines[targetIdx].unitPrice || created.costPrice || created.salePrice || 0,
        };
      } else {
        lines.push({ ...emptyLine, item: created._id, unitPrice: created.costPrice || created.salePrice || 0 });
      }
      return { ...prev, items: lines };
    });
  };

  const total = useMemo(() => (form.items || []).reduce((sum: number, l: any) => sum + (Number(l.quantity) || 0) * (Number(l.unitPrice) || 0), 0), [form.items]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Prefer selected supplier id; if not provided, pass newSupplier string for backend to resolve/create
    const payload = {
      supplier: form.supplier || form.newSupplier,
      expectedDate: form.expectedDate || undefined,
      notes: form.notes || undefined,
      items: (form.items || []).filter((l: any) => l.item && Number(l.quantity) > 0).map((l: any) => ({
        item: l.item,
        quantity: Number(l.quantity),
        unitPrice: Number(l.unitPrice),
      })),
    };
    if (!payload.supplier) return alert("Please select or enter a supplier.");
    if (!payload.items.length) return alert("Add at least one item with quantity.");
    await onSubmit(payload);
  };

  return (
    <form onSubmit={submit} className="space-y-6">
      {/* Supplier */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormControl fullWidth size="small">
          <InputLabel sx={{ color: "rgba(255,255,255,0.8)" }}>Supplier</InputLabel>
          <Select
            label="Supplier"
            value={form.supplier}
            onChange={(e) => setForm({ ...form, supplier: e.target.value as string })}
          >
            <MenuItem value=""><em>None</em></MenuItem>
            {suppliers.map(s => (
              <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField name="newSupplier" label="Or enter new supplier" value={form.newSupplier} onChange={handleField} fullWidth size="small" />
      </div>

      {/* Expected date + Notes */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TextField name="expectedDate" label="Expected Date" type="date" value={form.expectedDate} onChange={handleField} InputLabelProps={{ shrink: true }} fullWidth size="small" />
        <TextField name="notes" label="Notes" value={form.notes} onChange={handleField} fullWidth size="small" />
      </div>

      {/* Items table */}
      <div className={`rounded border ${dark ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-200'}`}>
        <table className={`w-full ${dark ? 'text-slate-100' : 'text-slate-900'}`}>
          <thead className={`${dark ? 'bg-slate-800' : 'bg-slate-50 text-slate-700'}`}>
            <tr>
              <th className="text-left px-3 py-2">Item</th>
              <th className="text-left px-3 py-2">Quantity</th>
              <th className="text-left px-3 py-2">Unit Price</th>
              <th className="text-left px-3 py-2">Subtotal</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className={`divide-y ${dark ? 'divide-slate-800' : 'divide-slate-200'}`}>
            {(form.items || []).map((line: any, idx: number) => {
                            return (
                <tr key={idx}>
                  <td className="px-3 py-2">
                    <FormControl fullWidth size="small">
                      <Select
                        value={line.item}
                        onChange={(e) => {
                          const id = e.target.value as string;
                          const found = items.find(i => i._id === id);
                          updateLine(idx, { item: id, unitPrice: line.unitPrice || found?.costPrice || found?.salePrice || 0 });
                        }}
                        displayEmpty
                      >
                        <MenuItem value=""><em>Select item</em></MenuItem>
                        {items.map(i => (
                          <MenuItem key={i._id} value={i._id}>{i.name} ({i.sku})</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </td>
                  <td className="px-3 py-2">
                    <TextField
                      type="number"
                      size="small"
                      value={line.quantity}
                      onChange={(e) => updateLine(idx, { quantity: Number(e.target.value) })}
                      inputProps={{ min: 0 }}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <TextField
                      type="number"
                      size="small"
                      value={line.unitPrice}
                      onChange={(e) => updateLine(idx, { unitPrice: Number(e.target.value) })}
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                  </td>
                  <td className="px-3 py-2">${((Number(line.quantity) || 0) * (Number(line.unitPrice) || 0)).toFixed(2)}</td>
                  <td className="px-3 py-2 text-right">
                    <IconButton color="error" onClick={() => removeLine(idx)} size="small"><DeleteIcon fontSize="small" /></IconButton>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="p-3 flex flex-wrap items-center justify-between gap-3">
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button startIcon={<AddIcon />} size="small" variant="outlined" onClick={addLine}>Add Item</Button>
            <Button startIcon={<AddIcon />} size="small" variant="text" onClick={() => setQuickAddOpen(true)}>
              New Inventory Item
            </Button>
          </Stack>
          <div className={`${dark ? 'text-slate-200' : 'text-slate-800'} font-semibold`}>Total: ${total.toFixed(2)}</div>
        </div>
      </div>

      {/* Submit */}
      <div className="flex justify-end">
        <Button type="submit" variant="contained" disabled={saving}>
          {saving ? "Saving..." : "Save Purchase Order"}
        </Button>
      </div>
      <InventoryQuickAddModal
        open={quickAddOpen}
        onClose={() => setQuickAddOpen(false)}
        onCreated={handleItemCreated}
      />
    </form>
  );
};

export default PurchaseOrderForm;

